import { motion, useAnimationFrame } from 'framer-motion'
import { useState } from 'react'
import DNABackground from './ADNBakground'

/* ─── variants ───────────────────────────────────── */
const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  show: (d = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.78, ease: [0.22, 1, 0.36, 1], delay: d },
  }),
}
const fadeIn = {
  hidden: { opacity: 0 },
  show: (d = 0) => ({ opacity: 1, transition: { duration: 0.6, ease: 'easeOut', delay: d } }),
}

/* ─── data ───────────────────────────────────────── */
const stats = [
  { value: '4.800k', label: 'Alumnos formados' },
  { value: '10M',    label: 'Visualizaciones'  },
  { value: '210k',   label: 'Seguidores'        },
  { value: '10k',    label: 'Años enseñando'   },
]

/* ─── helper: split "4.800k" → { digits:"4.800", suffix:"k" } ─── */
function parseStat(raw) {
  const match = raw.match(/^([\d.,]+)([kKmMbB]?)$/)
  return match
    ? { digits: match[1], suffix: match[2].toUpperCase() }
    : { digits: raw, suffix: '' }
}

/* ─── scroll arrow ───────────────────────────────── */
function ScrollArrow() {
  const [yVal, setYVal] = useState(0)
  useAnimationFrame((t) => setYVal(Math.sin(t / 700) * 4))
  return (
    <svg width="14" height="20" viewBox="0 0 14 20" fill="none"
      style={{ transform: `translateY(${yVal}px)` }}>
      <motion.line x1="7" y1="0" x2="7" y2="13"
        stroke="var(--gold)" strokeWidth="1" strokeOpacity="0.6"
        initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
        transition={{ duration: 1.1, delay: 1, ease: 'easeInOut' }}
      />
      <motion.polyline points="2,9 7,15 12,9"
        stroke="var(--gold)" strokeWidth="1" fill="none" strokeOpacity="0.8"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: 1.4 }}
      />
    </svg>
  )
}

/* ─── stat card ──────────────────────────────────── */
function StatCard({ value, label, delay, i }) {
  const { digits, suffix } = parseStat(value)

  return (
    <motion.div
      initial="hidden" animate="show" variants={fadeUp} custom={delay}
      whileHover={{ y: -3, transition: { duration: 0.22 } }}
      className="relative flex flex-col justify-center"
      style={{
        padding: 'clamp(13px,3.5vw,17px) clamp(12px,3vw,15px)',
        background: 'rgba(201,168,76,0.04)',
        border: '1px solid rgba(201,168,76,0.13)',
        backdropFilter: 'blur(4px)',
        minHeight: 'clamp(76px,17vw,92px)',
      }}
    >
      {/* animated L-corner accent */}
      <motion.span className="absolute"
        style={{ top: -1, left: 0, height: 1, background: '#C9A84C' }}
        initial={{ width: 0 }} animate={{ width: 24 }}
        transition={{ duration: 0.6, delay: delay + 0.1, ease: [0.22,1,0.36,1] }}
      />
      <motion.span className="absolute"
        style={{ top: 0, left: -1, width: 1, background: '#C9A84C' }}
        initial={{ height: 0 }} animate={{ height: 18 }}
        transition={{ duration: 0.5, delay: delay + 0.15, ease: [0.22,1,0.36,1] }}
      />

      {/* number row */}
      <div className="flex items-baseline" style={{ gap: 1 }}>
        {/* + prefix: small, muted */}
        <span className="font-display font-black"
          style={{ fontSize: 'clamp(0.7rem,3vw,0.88rem)', color: 'rgba(240,237,230,0.42)', lineHeight: 1 }}>
          +
        </span>
        {/* digits: white */}
        <span className="font-display font-black"
          style={{ fontSize: 'clamp(1.4rem,5.5vw,1.75rem)', color: 'var(--ink)', lineHeight: 1, letterSpacing: '-0.02em' }}>
          {digits}
        </span>
        {/* suffix: gold */}
        {suffix && (
          <span className="font-display font-black"
            style={{ fontSize: 'clamp(1.4rem,5.5vw,1.75rem)', color: 'var(--gold)', lineHeight: 1, letterSpacing: '-0.02em' }}>
            {suffix}
          </span>
        )}
        {/* pulsing dot */}
        <motion.span className="inline-block flex-shrink-0"
          style={{ width: 4, height: 4, borderRadius: '50%', background: 'var(--gold)', marginLeft: 4, marginBottom: 3 }}
          animate={{ opacity: [0.25, 0.9, 0.25], scale: [0.8, 1.2, 0.8] }}
          transition={{ duration: 2.6, repeat: Infinity, delay: i * 0.4 }}
        />
      </div>

      {/* label */}
      <span className="font-display uppercase block"
        style={{ fontSize: 'clamp(7px,1.8vw,8px)', color: 'var(--ink-3)', letterSpacing: '0.15em', marginTop: 6 }}>
        {label}
      </span>
    </motion.div>
  )
}

/* ─── gold divider ───────────────────────────────── */
function GoldLine({ delay }) {
  return (
    <motion.div
      style={{
        height: 1,
        background: 'linear-gradient(90deg, var(--gold) 0%, transparent 100%)',
        opacity: 0.2,
      }}
      initial={{ scaleX: 0, originX: 0 }}
      animate={{ scaleX: 1 }}
      transition={{ duration: 1, delay, ease: [0.22,1,0.36,1] }}
    />
  )
}

/* ─── Hero ───────────────────────────────────────── */
export default function Hero() {
  return (
    <section
      id="hero"
      className="relative overflow-hidden flex flex-col"
      style={{ minHeight: '100svh', background: 'var(--bg)' }}
    >
      {/* DNA canvas */}
      <div className="absolute inset-0 pointer-events-none z-0">
        <DNABackground />
      </div>

      {/* glows */}
      <div aria-hidden className="absolute pointer-events-none z-0" style={{
        right: '-8%', bottom: '-12%',
        width: 'min(80vw,660px)', height: 'min(80vw,660px)', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(201,168,76,0.10) 0%, rgba(201,168,76,0.04) 38%, transparent 68%)',
        filter: 'blur(72px)',
      }}/>
      <div aria-hidden className="absolute pointer-events-none z-0" style={{
        left: '-6%', top: '15%',
        width: 'min(55vw,420px)', height: 'min(55vw,420px)', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(201,168,76,0.055) 0%, transparent 65%)',
        filter: 'blur(90px)',
      }}/>
      <div aria-hidden className="absolute pointer-events-none z-0" style={{
        left: '30%', top: '35%',
        width: 'min(40vw,300px)', height: 'min(40vw,300px)', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(201,168,76,0.025) 0%, transparent 70%)',
        filter: 'blur(60px)',
      }}/>

      {/* vignette */}
      <div className="absolute inset-0 z-0 pointer-events-none" style={{
        background: 'linear-gradient(160deg, rgba(13,13,13,0.6) 0%, rgba(13,13,13,0.05) 45%, rgba(13,13,13,0.55) 100%)',
      }}/>

      {/* ── BODY ────────────────────────────────────── */}
      <div
        className="relative z-10 flex-1 grid grid-cols-1 lg:grid-cols-[1fr_auto] px-5 sm:px-8 lg:px-12"
        style={{ alignItems: 'center', gap: 'clamp(24px,4vw,48px)' }}
      >
        {/* LEFT col */}
        <div className="flex flex-col justify-between lg:justify-center h-full lg:h-auto py-4 lg:py-10">

          {/* eyebrow */}
          <motion.div
            className="flex items-center gap-3 mb-5 sm:mb-7"
            initial="hidden" animate="show" variants={fadeIn} custom={0.06}
          >
            <span style={{ width: 18, height: 1, background: 'var(--gold)', opacity: 0.75, flexShrink: 0 }}/>
            <span className="font-display uppercase"
              style={{ fontSize: 'clamp(7px,1.8vw,9px)', color: 'var(--gold)', opacity: 0.9, letterSpacing: '0.2em' }}>
              Medicina . Formación . Mentoría
            </span>
          </motion.div>

          {/* headline */}
          <motion.h1
            className="font-display font-black leading-[1.0] tracking-[-0.025em] mb-4 sm:mb-5"
            style={{ fontSize: 'clamp(2.1rem, 9vw, 4.2rem)', color: 'var(--ink)' }}
            initial="hidden" animate="show" variants={fadeUp} custom={0.1}
          >
            No necesitás<br/>
            <em className="not-italic" style={{ color: 'var(--gold)' }}>ser un genio</em><br/>
            para aprobar<br/>
            medicina.
          </motion.h1>

          <div className="mb-5 sm:mb-6">
            <GoldLine delay={0.2} />
          </div>

          {/* subheading */}
          <motion.p
            className="font-body font-light mb-6 sm:mb-8"
            style={{
              fontSize: 'clamp(0.85rem, 3vw, 1rem)',
              color: 'var(--ink-2)', lineHeight: 1.85,
              maxWidth: 'min(100%, 380px)',
            }}
            initial="hidden" animate="show" variants={fadeUp} custom={0.22}
          >
            Necesitás alguien que te explique bien.
          </motion.p>

          {/* CTAs */}
          <motion.div
            className="flex flex-wrap gap-2 sm:gap-3 mb-8 sm:mb-10"
            initial="hidden" animate="show" variants={fadeUp} custom={0.3}
          >
            <button className="font-display font-black uppercase"
              style={{
                fontSize: 'clamp(9px,2vw,11px)',
                padding: 'clamp(12px,3vw,14px) clamp(22px,5vw,28px)',
                letterSpacing: '0.12em',
                background: 'var(--gold)', color: '#000', border: 'none', cursor: 'pointer',
              }}>
              Ver cursos
            </button>
            <button className="font-display font-semibold uppercase"
              style={{
                fontSize: 'clamp(9px,2vw,11px)',
                padding: 'clamp(11px,3vw,13px) clamp(22px,5vw,28px)',
                letterSpacing: '0.1em',
                background: 'transparent', color: 'var(--ink)',
                border: '1px solid var(--line)', cursor: 'pointer',
              }}>
              Empezar ahora →
            </button>
          </motion.div>

          {/* stats 2×2 */}
          <div
            className="grid grid-cols-2"
            style={{ gap: 'clamp(8px,2vw,12px)', maxWidth: 'min(100%, 420px)' }}
          >
            {stats.map(({ value, label }, i) => (
              <StatCard key={i} value={value} label={label} delay={0.38 + i * 0.07} i={i} />
            ))}
          </div>
        </div>

        {/* RIGHT col: desktop only */}
        <div
          className="hidden lg:flex flex-col justify-center self-stretch py-10"
          style={{ width: 'clamp(200px,22vw,280px)' }}
        >
          <motion.div
            initial="hidden" animate="show" variants={fadeUp} custom={0.55}
            className="flex flex-col justify-center gap-7 h-full"
          >
            <div style={{ display: 'flex', gap: 14 }}>
              <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--gold)', opacity: 0.35, flexShrink: 0 }}/>
              <p className="font-body font-light leading-[1.95]"
                style={{ fontSize: 'clamp(0.78rem,1.4vw,0.88rem)', color: 'var(--ink-2)' }}>
                Clases de alto nivel que convierten temas complejos en
                razonamiento claro, aplicable a tus exámenes y a tu carrera.
              </p>
            </div>

            <motion.div
              initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.75, duration: 0.5, ease: [0.22,1,0.36,1] }}
              className="inline-flex items-center gap-2"
              style={{
                border: '1px solid rgba(201,168,76,0.2)',
                padding: '8px 12px',
                background: 'rgba(201,168,76,0.04)',
              }}
            >
              <motion.span
                style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--gold)', flexShrink: 0 }}
                animate={{ opacity: [0.4, 1, 0.4] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
              <span className="font-display uppercase"
                style={{ fontSize: 8, color: 'var(--gold)', letterSpacing: '0.15em', opacity: 0.85 }}>
                +12 años de experiencia
              </span>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* ── BOTTOM BAR ──────────────────────────────── */}
      <div className="relative z-10 flex items-center justify-between px-5 sm:px-8 lg:px-12 pb-6 sm:pb-8 pt-3 sm:pt-4">
        <motion.div className="flex items-center gap-3"
          initial="hidden" animate="show" variants={fadeIn} custom={0.85}>
          <ScrollArrow/>
          <span className="font-display uppercase tracking-[0.22em]"
            style={{ fontSize: 8, color: 'var(--ink-3)' }}>
            Scroll
          </span>
        </motion.div>

        <motion.div className="hidden sm:flex items-center gap-2"
          initial="hidden" animate="show" variants={fadeIn} custom={0.9}>
          <span style={{ width: 24, height: 1, background: 'var(--line)', opacity: 0.5 }}/>
          <span className="font-display uppercase"
            style={{ fontSize: 8, color: 'var(--ink-3)', letterSpacing: '0.15em' }}>
            6 cursos activos
          </span>
        </motion.div>
      </div>
    </section>
  )
}